// BuilderTabs.js - Grouped tab navigation for the guide builder (Step 2)
import React from 'react';
import {
  Building2, Globe, Info, MapPin, Map, Volume2, Languages,
  BookOpen, Sparkles, Package, Camera, ScanSearch, Edit2, Database, Megaphone,
  ClipboardCheck
} from 'lucide-react';

const TAB_GROUPS = [
  {
    label: 'Guide Content',
    tabs: [
      { id: 'metadata', label: 'Guide Info', icon: Info },
      { id: 'pois', label: 'Stops', icon: MapPin, showBadge: true },
      { id: 'edit-poi', label: 'Edit Stop', icon: Edit2, onlyWhenActive: true },
      { id: 'map', label: 'Map', icon: Map },
    ],
  },
  {
    label: 'Venue Pages',
    tabs: [
      { id: 'museum-info', label: 'Business Page', icon: Building2 },
      { id: 'venue-page', label: 'Venue Page', icon: Globe, requiresVenuePage: true },
    ],
  },
  {
    label: 'AI & Media',
    tabs: [
      { id: 'ai-guide-content', label: 'AI Guide Content', icon: Database },
      { id: 'media-package', label: 'Media Package', icon: Megaphone },
      { id: 'quality-review', label: 'Quality Review', icon: ClipboardCheck },
      { id: 'audio', label: 'Audio', icon: Volume2 },
      { id: 'translation', label: 'Translation', icon: Languages },
      { id: 'story-ai', label: 'Story AI', icon: Sparkles },
      { id: 'rag', label: 'Knowledge (RAG)', icon: BookOpen },
    ],
  },
  {
    label: 'Advanced',
    tabs: [
      { id: 'objects', label: 'Objects', icon: Package },
      { id: 'vpr', label: 'Room Recognition V2', icon: ScanSearch, accent: 'purple' },
      { id: 'room-recognition', label: 'Room Recognition V1 (old)', icon: Camera, accent: 'purple' },
    ],
  },
];

const BuilderTabs = ({ activeTab, onTabChange, showVenuePage, stopsCount }) => {
  const renderTab = (tab) => {
    if (tab.requiresVenuePage && !showVenuePage) return null;
    if (tab.onlyWhenActive && activeTab !== tab.id) return null;

    const Icon = tab.icon;
    const isActive = activeTab === tab.id;
    const isPurple = tab.accent === 'purple';

    let tabStyle;
    if (isPurple) {
      tabStyle = isActive
        ? 'bg-purple-600 text-white shadow-sm'
        : 'bg-purple-100 text-purple-700 hover:bg-purple-200';
    } else {
      tabStyle = isActive
        ? 'bg-secondary text-white shadow-sm'
        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100';
    }

    return (
      <button
        key={tab.id}
        onClick={() => onTabChange(tab.id)}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${tabStyle}`}
      >
        <Icon className="w-4 h-4" />
        {tab.label}
        {tab.showBadge && stopsCount > 0 && (
          <span className={`px-1.5 py-0.5 rounded-full text-xs font-semibold ${
            isActive ? 'bg-white/20 text-white' : 'bg-gray-200 text-gray-600'
          }`}>
            {stopsCount}
          </span>
        )}
      </button>
    );
  };

  return (
    <div className="border-b bg-gray-50 px-6 py-3">
      <div className="flex flex-wrap gap-x-8 gap-y-3">
        {TAB_GROUPS.map((group) => {
          const visibleTabs = group.tabs.map(renderTab).filter(Boolean);
          if (visibleTabs.length === 0) return null;

          return (
            <div key={group.label}>
              <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-1.5 px-1">
                {group.label}
              </div>
              <div className="flex flex-wrap gap-1">
                {visibleTabs}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default BuilderTabs;
